// Secure checkout — Authorize.net Accept.js tokenization (production). 2026 rebrand.
// Card data is dispatched directly to Authorize.net; only the payment nonce hits our backend.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MarketingShell from "../components/MarketingShell";
import { track } from "../lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;
const ACCEPT_JS_URL = "https://js.authorize.net/v1/Accept.js";
const AUTH_DATA = {
  apiLoginID: process.env.REACT_APP_AUTHNET_API_LOGIN_ID,
  clientKey: process.env.REACT_APP_AUTHNET_PUBLIC_CLIENT_KEY,
};

function useAcceptJs() {
  const [ready, setReady] = useState(!!window.Accept);
  useEffect(() => {
    if (window.Accept) return;
    const s = document.createElement("script");
    s.src = ACCEPT_JS_URL;
    s.charset = "utf-8";
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);
  return ready;
}

export default function Checkout() {
  const [params] = useSearchParams();
  const acceptReady = useAcceptJs();
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(params.get("product") || "");
  const [form, setForm] = useState({ name: "", email: "", cardNumber: "", month: "", year: "", cardCode: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);

  useEffect(() => { document.title = "Checkout | JC3 Wealth Legacy Group"; }, []);
  useEffect(() => {
    fetch(`${API}/api/payments/products`)
      .then((r) => r.json())
      .then((d) => {
        const list = d.products || [];
        setProducts(list);
        setProductId((cur) => (list.some((p) => p.id === cur) ? cur : (list[0]?.id ?? "")));
      })
      .catch(() => setError("Could not load products. Please refresh."));
  }, []);

  const selected = useMemo(() => products.find((p) => p.id === productId), [products, productId]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!acceptReady || !window.Accept) { setError("Secure payment library is still loading — try again in a moment."); return; }
    if (!selected) { setError("Please select a service."); return; }
    if (!form.name.trim() || !form.email.includes("@")) { setError("Please enter your name and a valid email."); return; }
    setBusy(true);
    track("checkout-pay-click", { product: selected.id }, "/checkout");
    window.Accept.dispatchData(
      {
        authData: AUTH_DATA,
        cardData: {
          cardNumber: form.cardNumber.replace(/\s/g, ""),
          month: form.month.padStart(2, "0"),
          year: form.year.length === 2 ? `20${form.year}` : form.year,
          cardCode: form.cardCode,
        },
      },
      async (response) => {
        if (response.messages.resultCode === "Error") {
          setError(response.messages.message[0]?.text || "Card validation failed.");
          setBusy(false);
          return;
        }
        try {
          const r = await fetch(`${API}/api/payments/charge`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              product_id: selected.id,
              opaqueData: response.opaqueData,
              customer: { name: form.name.trim(), email: form.email.trim() },
            }),
          });
          const data = await r.json();
          if (!r.ok) throw new Error(data.detail || "Payment was declined.");
          setReceipt(data);
        } catch (err) {
          setError(err.message);
        } finally {
          setBusy(false);
        }
      }
    );
  };

  return (
    <MarketingShell>
      <section className="page-head" aria-labelledby="h-checkout">
        <div className="wrap">
          <h1 id="h-checkout" className="display-xl">Complete your engagement</h1>
          <p className="lead dim-dark">Payments are processed by Authorize.net. Your card details never touch our servers.</p>
        </div>
      </section>
      <section className="band on-stone" data-testid="checkout-page">
        <div className="wrap">
          {receipt ? (
            <div className="panel" data-testid="checkout-receipt">
              <h2 className="h3">Payment approved</h2>
              <div className="small dim-light" style={{ display: "grid", gap: "var(--s2)" }}>
                <p><b>Service:</b> {receipt.product}</p>
                <p><b>Amount:</b> ${receipt.amount}</p>
                <p data-testid="receipt-transaction-id"><b>Transaction ID:</b> {receipt.transaction_id}</p>
              </div>
              <p className="small dim-light">A receipt will be emailed to you shortly. Our team will reach out within one business day to begin your engagement.</p>
            </div>
          ) : (
            <form className="panel" onSubmit={submit} data-testid="checkout-form">
              <p className="small dim-light secure-note">Financial Profile Development and Capital Strategy fees are never charged here up front. They are invoiced after the work is delivered, and you pay from the link in your invoice.</p>
              <div className="field">
                <label htmlFor="co-service">Service</label>
                <select id="co-service" data-testid="checkout-product-select" value={productId} onChange={(e) => setProductId(e.target.value)}>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} — ${p.amount}</option>
                  ))}
                </select>
              </div>
              <div className="form-grid">
                <div className="field"><label htmlFor="co-name">Full name</label><input id="co-name" data-testid="checkout-name-input" type="text" autoComplete="name" placeholder="Full name" value={form.name} onChange={set("name")} required /></div>
                <div className="field"><label htmlFor="co-email">Email</label><input id="co-email" data-testid="checkout-email-input" type="email" autoComplete="email" placeholder="Email" value={form.email} onChange={set("email")} required /></div>
                <div className="field full"><label htmlFor="co-card">Card number</label><input id="co-card" data-testid="checkout-card-input" type="text" inputMode="numeric" autoComplete="cc-number" placeholder="Card number" value={form.cardNumber} onChange={set("cardNumber")} required /></div>
                <div className="field"><label htmlFor="co-mm">Expiry month</label><input id="co-mm" data-testid="checkout-month-input" type="text" inputMode="numeric" autoComplete="cc-exp-month" placeholder="MM" maxLength={2} value={form.month} onChange={set("month")} required /></div>
                <div className="field"><label htmlFor="co-yy">Expiry year</label><input id="co-yy" data-testid="checkout-year-input" type="text" inputMode="numeric" autoComplete="cc-exp-year" placeholder="YYYY" maxLength={4} value={form.year} onChange={set("year")} required /></div>
                <div className="field"><label htmlFor="co-cvv">CVV</label><input id="co-cvv" data-testid="checkout-cvv-input" type="text" inputMode="numeric" autoComplete="cc-csc" placeholder="CVV" maxLength={4} value={form.cardCode} onChange={set("cardCode")} required /></div>
              </div>
              {error && <p className="small" style={{ color: "var(--oxblood)" }} data-testid="checkout-error">{error}</p>}
              <div>
                <button className="action" type="submit" id="co-pay" data-testid="checkout-pay-button" disabled={busy}>
                  {busy ? "Processing…" : `Pay ${selected ? `$${selected.amount}` : ""} securely`}
                </button>
              </div>
              <p className="small dim-light secure-note">PCI-compliant tokenization via Authorize.net Accept.js. We never store card numbers.</p>
            </form>
          )}
        </div>
      </section>
    </MarketingShell>
  );
}
